import { useEffect, useState } from 'react';
import fallbackServices from './services.json';
import { API_ENDPOINTS, apiCall } from './api';

// Where the invoice form's Description list comes from.
//
// Admins and super admins maintain it at /invoices/hsn-codes, so that is the
// source of truth. The bundled services.json stays as the fallback: if the
// call fails, or the table is still empty, the form must not be left without
// a list to pick from.

let cache = null;        // last list the API returned
let inflight = null;     // in-flight request, so callers share one round trip

const normalize = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r) => ({
      label: String(r?.label ?? '').trim(),
      value: String(r?.value ?? '').trim(),
      hsn: String(r?.hsn ?? '').trim(),
      gst: String(r?.gst ?? '').trim() || '18',
    }))
    .filter((r) => r.label && r.value);
};

/** Fetch the list once and remember it. Falls back to the bundled file. */
export const loadServices = async ({ force = false } = {}) => {
  if (!force && cache) return cache;
  if (!force && inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await apiCall(API_ENDPOINTS.HSN_CODES, { method: 'GET' });
      const data = await res.json().catch(() => ({}));
      const rows = normalize(res.ok && data?.success !== false ? data?.data : null);
      if (rows.length) cache = rows;
    } catch {
      // keep whatever we had; getServices() falls back on its own
    } finally {
      inflight = null;
    }
    return cache || fallbackServices;
  })();

  return inflight;
};

/** The current list, without waiting — the API's if it has arrived. */
export const getServices = () => cache || fallbackServices;

/** Drop the cache after an admin edits the list. */
export const invalidateServices = () => { cache = null; inflight = null; };

/** The list for components; re-renders once the API answers. */
export const useServices = () => {
  const [services, setServices] = useState(getServices);

  useEffect(() => {
    let cancelled = false;
    loadServices().then((rows) => { if (!cancelled) setServices(rows); });
    return () => { cancelled = true; };
  }, []);

  return services;
};

export default useServices;
